import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { PWA_UPDATE_EVENT } from "./components/common/PwaUpdateNotification";
import { currentTenant, currentTenantId } from "./tenants";
import fsmFavicon from "./assets/tenants/fsm/favicon-32x32.png";
import fsmAppleTouchIcon from "./assets/tenants/fsm/apple-touch-icon.png";
import fsmIcon192 from "./assets/tenants/fsm/android-chrome-192x192.png";
import fsmIcon512 from "./assets/tenants/fsm/android-chrome-512x512.png";
import ilParrocchialeFavicon from "./assets/tenants/il_parrocchiale/favicon-32x32.png";
import ilParrocchialeAppleTouchIcon from "./assets/tenants/il_parrocchiale/apple-touch-icon.png";
import ilParrocchialeIcon192 from "./assets/tenants/il_parrocchiale/android-chrome-192x192.png";
import ilParrocchialeIcon512 from "./assets/tenants/il_parrocchiale/android-chrome-512x512.png";

// Il manifest viene costruito a runtime usando le URL (con hash) degli asset
// importati: importarlo come file statico farebbe risolvere le icone con path
// relativi inesistenti (es. /static/media/android-chrome-192x192.png -> 404).
const buildManifestUrl = (manifest: {
  name: string;
  short_name: string;
  description: string;
  theme_color: string;
  background_color: string;
  icon192: string;
  icon512: string;
}) => {
  // Il manifest è servito come blob: URL, che non ha una base valida per
  // risolvere URL relative. Quindi start_url/scope/icone devono essere ASSOLUTE,
  // altrimenti Chrome le scarta ("property ... ignored, URL is invalid").
  const appBase = new URL(
    process.env.PUBLIC_URL || "/",
    window.location.origin
  ).href;
  const toAbsolute = (path: string) =>
    new URL(path, window.location.origin).href;

  const blob = new Blob(
    [
      JSON.stringify({
        name: manifest.name,
        short_name: manifest.short_name,
        description: manifest.description,
        start_url: appBase,
        scope: appBase,
        display: "standalone",
        theme_color: manifest.theme_color,
        background_color: manifest.background_color,
        icons: [
          {
            src: toAbsolute(manifest.icon192),
            sizes: "192x192",
            type: "image/png",
          },
          {
            src: toAbsolute(manifest.icon512),
            sizes: "512x512",
            type: "image/png",
          },
        ],
      }),
    ],
    { type: "application/manifest+json" }
  );
  return URL.createObjectURL(blob);
};

const tenantIcons = {
  fsm: {
    favicon: fsmFavicon,
    appleTouchIcon: fsmAppleTouchIcon,
    manifest: buildManifestUrl({
      name: "Societa Sportiva FSM",
      short_name: "S.S. FSM",
      description: "Applicazione web della Societa Sportiva FSM.",
      theme_color: "#1d4ed8",
      background_color: "#1d4ed8",
      icon192: fsmIcon192,
      icon512: fsmIcon512,
    }),
  },
  il_parrocchiale: {
    favicon: ilParrocchialeFavicon,
    appleTouchIcon: ilParrocchialeAppleTouchIcon,
    manifest: buildManifestUrl({
      name: "Il Parrocchiale",
      short_name: "Il Parrocchiale",
      description: "Applicazione web de Il Parrocchiale.",
      theme_color: "#ffd60a",
      background_color: "#ffd60a",
      icon192: ilParrocchialeIcon192,
      icon512: ilParrocchialeIcon512,
    }),
  },
};

const setHeadLink = (rel: string, href: string) => {
  let link = document.head.querySelector(`link[rel="${rel}"]`) as
    | HTMLLinkElement
    | null;

  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
};

const setHeadMeta = (name: string, content: string) => {
  let meta = document.head.querySelector(`meta[name="${name}"]`) as
    | HTMLMetaElement
    | null;

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }

  meta.content = content;
};

const currentTenantIcons =
  tenantIcons[currentTenantId] ?? tenantIcons.fsm;

document.title = currentTenant.companyDisplayName;
setHeadLink("icon", currentTenantIcons.favicon);
setHeadLink("apple-touch-icon", currentTenantIcons.appleTouchIcon);
setHeadLink("manifest", currentTenantIcons.manifest);
setHeadMeta("theme-color", currentTenant.theme.navbarBg);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Abilita la PWA (offline + installazione). Attivo solo in build di produzione.
serviceWorkerRegistration.register({
  // Nuova versione in cache: notifica l'utente (toast "Ricarica").
  onUpdate: () => window.dispatchEvent(new CustomEvent(PWA_UPDATE_EVENT)),
});
